import { Temporal } from '@js-temporal/polyfill'
import { v4 as uuid } from 'uuid'
import * as z from 'zod'

import type { Service, Tag as TagType } from '../../graphqlTypes'
import { AttachmentTypes, Services } from '~publish/legacy/constants'

import type { ListItem } from '../PostItems'
import type { Post } from '../PostItems/helpers'

const preprocessEmpty = (v: unknown): unknown =>
  typeof v === 'string' && v.trim() === '' ? undefined : v

const optionalString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined

export type ValidHeader =
  | 'text'
  | 'tags'
  | 'image_url'
  | 'posting_time'
  | 'board_name'
  | 'title'
  | 'description'
  | 'alt_text'
  | 'link'

export type ValidUserFriendlyHeader =
  | 'Text'
  | 'Tags'
  | 'Image URL'
  | 'Posting Time'
  | 'Board Name'
  | 'Title'
  | 'Description'
  | 'Alt Text'
  | 'Link'

export const userFriendlyHeaderMap: Record<
  ValidHeader,
  ValidUserFriendlyHeader
> = {
  text: 'Text',
  tags: 'Tags',
  image_url: 'Image URL',
  posting_time: 'Posting Time',
  board_name: 'Board Name',
  title: 'Title',
  description: 'Description',
  alt_text: 'Alt Text',
  link: 'Link',
}

// Shape produced after validating a CSV line
export type UserFriendlyCsvLine = {
  Text: string | undefined
  'Image URL': string | undefined
  Tags: string | undefined
  'Posting Time': string | undefined
  'Board Name'?: string
  Title?: string
  Description?: string
  'Alt Text'?: string
  Link?: string
}

export type FormattedCsvLine = {
  text: string | undefined
  image_url: string | undefined
  tags: string | undefined
  posting_time: string | undefined
  board_name?: string
  title?: string
  description?: string
  alt_text?: string
  link?: string
}

export type BulkPostSchemaOptions = {
  charLimit?: number
  requireText?: boolean
  requireImageUrl?: boolean
  requireTextOrImage?: boolean
  requireBoardName?: boolean
  includePinterestFields?: boolean
}

export const createCsvLineSchema = ({
  charLimit = 500,
  requireText = false,
  requireImageUrl = false,
  requireTextOrImage = true,
  requireBoardName = false,
  includePinterestFields = false,
}: BulkPostSchemaOptions = {}): z.ZodSchema<FormattedCsvLine> => {
  // TEXT field
  const textSchema = z
    .string('Text is required')
    .trim()
    .max(charLimit, `Text must not exceed ${charLimit} characters`)

  const text = z.preprocess(
    preprocessEmpty,
    requireText ? textSchema : textSchema.or(z.undefined()),
  )

  // IMAGE URL field
  const imageSchema = z.url({
    error: (iss) =>
      iss.input === undefined
        ? 'Image URL is required'
        : 'Invalid image URL format',
  })

  const image = z.preprocess(
    preprocessEmpty,
    requireImageUrl ? imageSchema : imageSchema.or(z.undefined()),
  )

  const postingTimeSchema = z
    .string()
    .trim()
    .optional()
    .default('Next Available')

  const tagsSchema = z.string().or(z.undefined())
  const tags = z.preprocess(preprocessEmpty, tagsSchema)

  // BOARD NAME field
  const boardName = z.string('Board Name is required for Pinterest').trim()

  // Build schema fields, conditionally adding optional fields
  const schemaFields: Record<string, z.ZodTypeAny> = {
    text,
    image_url: image,
    tags,
    posting_time: postingTimeSchema,
  }

  if (requireBoardName) {
    schemaFields.board_name = z.preprocess(preprocessEmpty, boardName)
  }

  if (includePinterestFields) {
    schemaFields.title = z.preprocess(
      preprocessEmpty,
      z
        .string()
        .trim()
        .max(100, 'Title must not exceed 100 characters')
        .or(z.undefined()),
    )
    // Legacy alias for text — column removed from the template (CORE-5761)
    schemaFields.description = z.preprocess(
      preprocessEmpty,
      z
        .string()
        .trim()
        .max(800, 'Description must not exceed 800 characters')
        .or(z.undefined()),
    )
    schemaFields.alt_text = z.preprocess(
      preprocessEmpty,
      z
        .string()
        .trim()
        .max(500, 'Alt Text must not exceed 500 characters')
        .or(z.undefined()),
    )
    schemaFields.link = z.preprocess(
      preprocessEmpty,
      z.url({ error: 'Invalid Link URL format' }).or(z.undefined()),
    )
  }

  const schema = z.object(schemaFields).transform((data): FormattedCsvLine => {
    const formatted: FormattedCsvLine = {
      text: optionalString(data.text),
      image_url: optionalString(data.image_url),
      tags: optionalString(data.tags),
      posting_time: optionalString(data.posting_time),
    }

    const optionalFields = [
      'board_name',
      'title',
      'description',
      'alt_text',
      'link',
    ] satisfies readonly (keyof FormattedCsvLine)[]
    optionalFields.forEach((field) => {
      const value = optionalString(data[field])
      if (value !== undefined) formatted[field] = value
    })

    return formatted
  })

  // ensure at least one when requested
  return requireTextOrImage
    ? schema.refine((data) => Boolean(data.text) || Boolean(data.image_url), {
        message: 'Either Text or Image URL must be provided',
      })
    : schema
}

const getValidationOptions = (
  channelService: Service,
  isTwitterPremium: boolean = false,
  mastodonMaxCharacters?: number,
): BulkPostSchemaOptions => {
  const service = Services.get(channelService)
  const charLimit =
    typeof service?.charLimit === 'function'
      ? service.charLimit({ isTwitterPremium, mastodonMaxCharacters })
      : service?.charLimit
  const requireText = service?.requiresText ?? false
  const requireImageUrl =
    service?.requiredAttachmentType === AttachmentTypes.MEDIA
  return {
    charLimit: charLimit ?? 500,
    requireText,
    requireImageUrl,
    requireTextOrImage: !requireText && !requireImageUrl,
    requireBoardName: channelService === 'pinterest',
    includePinterestFields: channelService === 'pinterest',
  }
}

const parseTags = (
  importedTags: string | undefined,
  existingTags: TagType[],
): { tags: TagType[]; errors: string[] } => {
  const rowTags = importedTags
    ? importedTags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    : []
  const existingTagMap = new Map(existingTags.map((tag) => [tag.name, tag]))

  const { actualTags, missingTags } = rowTags.reduce<{
    actualTags: TagType[]
    missingTags: string[]
  }>(
    (acc, tagName) => {
      const found = existingTagMap.get(tagName)
      if (found) {
        acc.actualTags.push(found)
      } else {
        acc.missingTags.push(tagName)
      }
      return acc
    },
    { actualTags: [], missingTags: [] },
  )

  const errors: string[] = []
  if (missingTags.length > 0) {
    errors.push(
      `Could not find tag${
        missingTags.length > 1 ? 's' : ''
      }: ${missingTags.join(', ')}`,
    )
  }
  return { tags: actualTags, errors }
}

/**
 * Parse and validate a posting time string in "YYYY-MM-DD HH:MM" format
 */
export const parsePostingTime = (
  postingTime: string | undefined,
  timezone: string,
): {
  postingTime: string
  error: string | undefined
} => {
  if (!postingTime) {
    return { postingTime: 'Next Available', error: undefined }
  }

  if (postingTime === 'Prioritize' || postingTime === 'Next Available') {
    return { postingTime, error: undefined }
  }

  const match = postingTime.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{1,2})$/,
  )
  if (!match) {
    return {
      postingTime,
      error:
        'Invalid Posting Time format. Please use YYYY-MM-DD HH:MM (24-hour format).',
    }
  }

  const [_, year, month, day, hour, minute] = match

  // Month-aware day validation
  const yearNum = Number(year)
  const monthNum = Number(month)
  const dayNum = Number(day)

  if (monthNum < 1 || monthNum > 12) {
    return {
      postingTime,
      error: `Please specify a month between 01-12`,
    }
  }

  // Days in each month, index 1-12
  const daysInMonth = [
    0,
    31,
    yearNum % 4 === 0 && (yearNum % 100 !== 0 || yearNum % 400 === 0) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ]
  if (dayNum < 1 || dayNum > daysInMonth[monthNum]) {
    return {
      postingTime,
      error: `Please specify a valid day value`,
    }
  }

  if (Number(hour) < 0 || Number(hour) > 23) {
    return {
      postingTime,
      error: `Please specify a valid hour value`,
    }
  }

  if (Number(minute) < 0 || Number(minute) > 59) {
    return {
      postingTime,
      error: `Please specify a valid minute value`,
    }
  }

  try {
    // Ensure all components are zero-padded for ISO format
    const paddedMonth = month.padStart(2, '0')
    const paddedDay = day.padStart(2, '0')
    const paddedHour = hour.padStart(2, '0')
    const paddedMinute = minute.padStart(2, '0')

    const parsedPostingTime = `${year}-${paddedMonth}-${paddedDay} ${paddedHour}:${paddedMinute}`
    const plainDateTime = Temporal.PlainDateTime.from(
      parsedPostingTime.replace(/\s+/, 'T'),
    )
    const zonedDateTime = plainDateTime.toZonedDateTime(timezone)
    const zonedNow = Temporal.Now.zonedDateTimeISO(timezone)
    // @js-temporal/polyfill v0.5 removed the epochSeconds accessor; compare the
    // two instants directly, which also gives full precision.
    if (Temporal.ZonedDateTime.compare(zonedDateTime, zonedNow) < 0) {
      return {
        postingTime,
        error: 'Posting Time must be in the future',
      }
    }
    return {
      postingTime: parsedPostingTime,
      error: undefined,
    }
  } catch {
    return {
      postingTime,
      error:
        'Invalid Posting Time format. Please use YYYY-MM-DD HH:MM (24-hour format).',
    }
  }
}

const parseBoardName = (
  boardName: string | undefined,
  existingBoards: Array<{ id: string; name: string }>,
): {
  board: { id: string; name: string } | undefined
  error: string | undefined
} => {
  const board = existingBoards.find(
    (candidateBoard) => candidateBoard.name === boardName?.trim(),
  )
  if (!board) {
    return { board: undefined, error: `Could not find board: ${boardName}` }
  }

  return { board, error: undefined }
}

export const validateAndConstructItem = ({
  row,
  channelId,
  channelService,
  channelTimezone,
  isTwitterPremium = false,
  mastodonMaxCharacters,
  existingTags = [],
  existingBoards = [],
}: {
  row: FormattedCsvLine
  channelId: string
  channelService: Service
  channelTimezone: string
  isTwitterPremium: boolean
  mastodonMaxCharacters?: number
  existingTags: TagType[]
  existingBoards: Array<{ id: string; name: string }>
}): ListItem => {
  const validationSchema = createCsvLineSchema(
    getValidationOptions(
      channelService,
      isTwitterPremium,
      mastodonMaxCharacters,
    ),
  )
  const validationResult = validationSchema.safeParse(row)
  const success = validationResult.success
  const parsedRow = success ? validationResult.data : row
  const errorMessages =
    validationResult.success === false
      ? validationResult.error.issues.map((issue) => issue.message)
      : []

  const { tags: validTags, errors: tagErrors } = parseTags(
    parsedRow.tags,
    existingTags,
  )

  errorMessages.push(...tagErrors)

  // Store the posting time string for display and later parsing

  const { postingTime, error: postingTimeError } = parsePostingTime(
    parsedRow.posting_time,
    channelTimezone,
  )
  if (postingTimeError) {
    errorMessages.push(postingTimeError)
  }

  const postData: Post = {
    profile_ids: [channelId],
    text: parsedRow.text,
    media: parsedRow.image_url ? { photo: parsedRow.image_url } : undefined,
    tags: validTags,
    // Always set created_source to bulk_upload here for BE Post Created tracking
    created_source: 'bulk_upload' as const,
    postingTime,
  }

  if (channelService === 'pinterest' && parsedRow.board_name?.trim()) {
    const { board, error } = parseBoardName(
      parsedRow.board_name,
      existingBoards,
    )
    if (error) {
      errorMessages.push(error)
    } else {
      postData.board = board
    }
  }

  if (channelService === 'pinterest') {
    if (parsedRow.title) postData.title = parsedRow.title
    // Legacy alias for Text: the Description column was removed from the
    // Pinterest template, but CSVs from older templates may still use it.
    if (parsedRow.description) {
      if (parsedRow.text && parsedRow.text !== parsedRow.description) {
        errorMessages.push(
          'Text and Description can’t both be filled in — Pinterest pins have a single description, which Buffer takes from the Text column. Move your pin description to Text and remove the Description column.',
        )
      } else {
        postData.text = parsedRow.description
      }
    }
    if (parsedRow.alt_text) postData.alt_text = parsedRow.alt_text
    if (parsedRow.link) postData.source_url = parsedRow.link
  }

  // Check if there are any errors (validation errors or missing tags)
  const hasErrors = !success || errorMessages.length > 0

  return {
    id: uuid(),
    data: postData,
    status: hasErrors ? 'error' : 'valid',
    errors: errorMessages,
  }
}
